/**
 * Turn an API error body into one readable sentence for a toast.
 *
 * FastAPI answers in three shapes: `detail` as a string (an HTTPException
 * with text), `detail` as an object (`{error: "snake_case"}` — the admin and
 * crew convention — optionally with a `message`), and `detail` as a list of
 * validation errors (422). Codes are humanized ("campaign_not_editable" →
 * "Campaign not editable") so the UI never shows "Request failed" for an
 * error the server actually explained.
 */

const DEFAULT_FALLBACK = "Request failed";

// Pydantic v2 prefixes custom validator messages; the text after it is the
// sentence the validator wrote for people.
const PYDANTIC_PREFIX = /^Value error, /;

function nonEmpty(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

export function humanizeErrorCode(code: string): string {
  const words = code.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!words) return DEFAULT_FALLBACK;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function apiErrorMessage(
  body: unknown,
  fallback: string = DEFAULT_FALLBACK,
): string {
  if (!body || typeof body !== "object") return fallback;
  const detail = (body as { detail?: unknown }).detail;

  if (nonEmpty(detail)) return detail;

  if (Array.isArray(detail)) {
    for (const item of detail) {
      const msg = (item as { msg?: unknown } | null)?.msg;
      if (nonEmpty(msg)) return msg.replace(PYDANTIC_PREFIX, "");
    }
    return fallback;
  }

  if (detail && typeof detail === "object") {
    const { message, error } = detail as { message?: unknown; error?: unknown };
    if (nonEmpty(message)) return message;
    if (nonEmpty(error)) return humanizeErrorCode(error);
  }

  return fallback;
}
